// src/actions/enrollment_actions.js
import * as API from "../util/enrollment_api_util";
import { uploadBloodborneCert } from "../util/users_util";
import { receiveCurrentUser } from "../actions/session_actions";

export const RECEIVE_MY_ENROLLMENTS = "RECEIVE_MY_ENROLLMENTS";
export const UPDATE_ENROLLMENT = "UPDATE_ENROLLMENT";

// (legacy / optional)
export const RECEIVE_ENROLLMENT = "RECEIVE_ENROLLMENT";

export const receiveMyEnrollments = (enrollments) => ({
  type: RECEIVE_MY_ENROLLMENTS,
  enrollments,
});

export const updateEnrollment = (enrollment) => ({
  type: UPDATE_ENROLLMENT,
  enrollment,
});

export const receiveEnrollment = (enrollment) => ({
  type: RECEIVE_ENROLLMENT,
  enrollment,
});

// Helper: normalize backend shapes into a plain array
const normalizeEnrollmentList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.enrollments)) return data.enrollments;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

// Helper: detect "soft expired" response shape (from our axios client)
const isSoftExpiredResponse = (res) => {
  return !!res?.__softExpired || res?.status === 401 || res?.data == null;
};

// Thunk: load current student's enrollments
export const fetchMyEnrollments = () => async (dispatch) => {
  try {
    const res = await API.fetchMyEnrollments();

    // ✅ Session expired -> modal already shown by util; do nothing + don't throw
    if (isSoftExpiredResponse(res)) return [];

    const list = normalizeEnrollmentList(res.data);
    dispatch(receiveMyEnrollments(list));
    return list;
  } catch (err) {
    // ✅ If any other code path still throws on 401, swallow it
    const status = err?.response?.status;
    if (status === 401) return [];

    console.error("Error fetching my enrollments:", err);
    throw err;
  }
};

// Master/GM: load a student's enrollments (for profile panel progress)
// NOTE: return only, don't dispatch into "my enrollments"
export const fetchEnrollmentsForUser = (userId) => async () => {
  try {
    const res = await API.fetchEnrollmentsForUser(userId);

    // ✅ if GM's session expired, just return empty
    if (isSoftExpiredResponse(res)) return [];

    const list = normalizeEnrollmentList(res.data);
    return list;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) return [];

    console.error("Error fetching enrollments for user:", err);
    throw err;
  }
};

// Thunk: generic progress update
export const updateEnrollmentProgress = (enrollmentId, data) => async (dispatch) => {
  try {
    const res = await API.updateEnrollmentProgress(enrollmentId, data);

    // ✅ expired -> do nothing
    if (isSoftExpiredResponse(res)) return null;

    dispatch(updateEnrollment(res.data));
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) return null;

    console.error("Error updating enrollment progress:", err);
    throw err;
  }
};

// Thunk: mark a section quiz as passed
export const markSectionQuizPassed = (enrollmentId, sectionId) => async (dispatch) => {
  try {
    const res = await API.markSectionQuizPassed(enrollmentId, sectionId);

    if (isSoftExpiredResponse(res)) return null;

    dispatch(updateEnrollment(res.data));
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) return null;

    console.error("Error marking section quiz passed:", err);
    throw err;
  }
};

// ✅ Student submits model info (name/phone/email)
export const submitModelInfo = (enrollmentId, payload) => async (dispatch) => {
  try {
    const res = await API.submitModelInfo(enrollmentId, payload);

    if (isSoftExpiredResponse(res)) return null;

    const enrollment = res.data?.enrollment || res.data;
    dispatch(updateEnrollment(enrollment));
    return enrollment;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) return null;

    console.error("Error submitting model info:", err);
    throw err;
  }
};

export const updatePhotoPackageSelection = (enrollmentId, selection) => async (dispatch) => {
  try {
    const res = await API.updatePhotoPackageSelection(enrollmentId, selection);

    if (isSoftExpiredResponse(res)) return null;

    const enrollment = res.data?.enrollment || res.data;
    if (enrollment?._id) dispatch(updateEnrollment(enrollment));
    return enrollment;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) return null;

    console.error("Error updating photo package selection:", err);
    throw err;
  }
};

export const updatePhotoPackageLink = (enrollmentId, deliveryLink) => async (dispatch) => {
  try {
    const res = await API.updatePhotoPackageLink(enrollmentId, deliveryLink);

    if (isSoftExpiredResponse(res)) return null;

    const enrollment = res.data?.enrollment || res.data;
    if (enrollment?._id) dispatch(updateEnrollment(enrollment));
    return enrollment;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) return null;

    console.error("Error updating photo package link:", err);
    throw err;
  }
};

// ✅ Student uploads bloodborne cert (multipart) — LEGACY (per enrollment)
export const submitBloodborneCert = (enrollmentId, file) => async (dispatch) => {
  try {
    const res = await API.submitBloodborneCert(enrollmentId, file);

    if (isSoftExpiredResponse(res)) return null;

    const enrollment = res.data?.enrollment || res.data;
    dispatch(updateEnrollment(enrollment));
    return enrollment;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) return null;

    console.error("Error uploading bloodborne cert:", err);
    throw err;
  }
};

/**
 * ✅ NEW: Student uploads bloodborne cert ONCE (global on User)
 * Expects backend route: POST /api/users/me/bloodborne-cert
 * util: uploadBloodborneCert(file)
 *
 * This updates session.user so every course sees it immediately.
 */
export const submitGlobalBloodborneCert = (file) => async (dispatch) => {
  try {
    const res = await uploadBloodborneCert(file);
    const updatedUser = res?.data?.user || res?.data?.currentUser || null;

    if (updatedUser) dispatch(receiveCurrentUser(updatedUser));

    return res?.data || null;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) return null;

    console.error("Error uploading GLOBAL bloodborne cert:", err);
    throw err;
  }
};

// ✅ Master/GM create or update an enrollment
export const createOrUpdateEnrollment = (payload) => async (dispatch) => {
  try {
    const res = await API.createOrUpdateEnrollment(payload);

    if (isSoftExpiredResponse(res)) return null;

    const enrollment = res.data?.enrollment || res.data;
    dispatch(updateEnrollment(enrollment));
    return enrollment;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) return null;

    console.error("Error creating/updating enrollment:", err);
    throw err;
  }
};
